import React, { useEffect, useState } from 'react';
import { CommonPageWrapper, HeadingWrapper } from 'admin/components/wrappers';
import AdminCustomButton from 'app/components/AdminCustomButton';
import { AdminSearch } from 'app/components/AdminSearchField';
import CustomBreadCrumb from 'app/components/CustomBreadCrumb';
import { BlurredLabel, PageMainLabel } from 'app/components/SHLabel';
import RegistrationReportTable from './RegistrationTable';
import { useRegistrationReport } from 'admin/hooks/useRegistrationReport';
import { useDebounce } from 'hooks/useDebounce';
import AdminDropDown from 'app/components/AdminDropDown';
import { AdminDatePicker } from 'app/components/AdminDatePicker';
import { GeneralLookup } from 'services/openapi';
import styled from 'styled-components';
import { GridPageChangeEvent } from '@progress/kendo-react-grid';
import { DatePickerChangeEvent } from '@progress/kendo-react-dateinputs';
import { TableSkeletonLoader } from 'app/components/TableSkeletonLoader';
import usePaymentApi from 'hooks/usePaymentApi';
import { extractFeaturesForPlan } from 'utils/common';
import { planIdsToRemove } from '../SettingsPage/SettingsCard/Components/SettingsPages/FeeManagementPage/FeeManagementTable';
import { usePermission } from 'hooks/usePermission';

const RegistrationReportPage = () => {
    const breadCrumbItems = [
        {
            id: '',
            text: 'Showyeda coach',
            disabled: true,
        },
        {
            id: '',
            text: 'Riders',
            disabled: true,
        },
    ];
    const { getPaymentPlansList, paymentPlansList, loading: isPaymentLoading } =
        usePaymentApi();
    const {
        fetchRegistrationReport,
        registrationReportData,
        downloadRegistrationReport,
        loading,
        downloadLoading,
        type,
        setType,
        fromDate,
        setFromDate,
        toDate,
        setToDate,
        pageNumber,
        setPageNumber,
        pageSize,
        setPageSize,
    } = useRegistrationReport();
    const [pageSizeSelected, setPageSizeSelected] = useState<GeneralLookup>({
        id: '50',
        name: '50',
    });
    const [localSearchTerm, setLocalSearchTerm] = useState<string>('');
    const debounceSearch = useDebounce(localSearchTerm);
    const { hasViewPermission } = usePermission();

    // Transform the payment plans data - Each plan as individual row
    const transformedData = React.useMemo(() => {
        const result: { id: string; name: string }[] = [];
        paymentPlansList?.forEach(group => {
            if (!group || !group.plans) return;

            group.plans.forEach(plan => {
                // Filter out plans with IDs in the removal list
                if (!plan?.id || planIdsToRemove.includes(plan.id)) {
                    return;
                }

                if (plan && plan.type?.name) {
                    // Extract features for this individual plan
                    const features = extractFeaturesForPlan(plan);
                    let displayName: string;

                    if (features.length > 0) {
                        // If features exist, show plan type with features
                        displayName = `${plan.type.name} (${features.join(', ')})`;
                    } else {
                        // If no features are true, show just the plan type name
                        displayName = plan.type.name;
                    }

                    result.push({
                        id: plan.id,
                        name: `${displayName} - $${plan.amount || 0} `,
                    });
                }
            });
        });

        return result;
    }, [paymentPlansList]);

    // Page size options for the dropdown
    const pageSizeOptions: GeneralLookup[] = [
        { id: '25', name: '25' },
        { id: '50', name: '50' },
        { id: '100', name: '100' },
        { id: '200', name: '200' },
    ];
    // Type filter options
    const typeOptions: GeneralLookup[] = [
        { id: '', name: 'None' },
        { id: 'Rider', name: 'Rider' },
        { id: 'Team', name: 'Team' },
        { id: 'Coach', name: 'Coach' },
        { id: 'Associate', name: 'Associate' },
    ];

    const handleDownloadRegistrationReport = async () => {
        try {
            const url = (await downloadRegistrationReport(
                type,
                fromDate,
                toDate,
                debounceSearch,
            )) as string;

            // Extract filename from URL
            const filename = url.split('/').pop();

            fetch(url)
                .then(response => response.blob())
                .then(blob => {
                    const objectUrl = URL.createObjectURL(blob);

                    const link = document.createElement('a');
                    link.href = objectUrl;
                    link.download = filename!;

                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(objectUrl);
                })
                .catch(error => {
                    console.error('Error downloading file:', error);
                });

            return url;
        } catch (error) {
            console.error('Error downloading report:', error);
            throw error;
        }
    };

    const handleTypeChange = (data: GeneralLookup) => {
        setType(data?.id || '');
        fetchRegistrationReport(
            data?.id || '',
            fromDate,
            toDate,
            debounceSearch,
            1,
            pageSize,
        );
    };

    const handleFromDateChange = (e: DatePickerChangeEvent) => {
        const date = e.value;
        setFromDate(date ? date.toISOString().split('T')[0] : '');
        fetchRegistrationReport(
            type,
            date ? date.toISOString().split('T')[0] : '',
            toDate,
            debounceSearch,
            1,
            pageSize,
        );
    };

    const handleToDateChange = (e: DatePickerChangeEvent) => {
        const date = e.value;
        setToDate(date ? date.toISOString().split('T')[0] : '');
        fetchRegistrationReport(
            type,
            fromDate,
            date ? date.toISOString().split('T')[0] : '',
            debounceSearch,
            1,
            pageSize,
        );
    };

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLocalSearchTerm(e.target.value);
    };

    const handleTablePageChange = (event: GridPageChangeEvent) => {
        const newPageNumber = Math.floor(event.page.skip / pageSize) + 1;
        setPageNumber(newPageNumber);
        fetchRegistrationReport(
            type,
            fromDate,
            toDate,
            debounceSearch,
            newPageNumber,
            pageSize,
        );
    };

    // Trigger search when debounced value changes
    useEffect(() => {
        if (fetchRegistrationReport) {
            fetchRegistrationReport(
                type,
                fromDate,
                toDate,
                debounceSearch,
                1,
                pageSize,
            );
        }
    }, [debounceSearch, fromDate, pageSize, toDate, type]);

    useEffect(() => {
        fetchRegistrationReport();
        getPaymentPlansList();
    }, []);

    return (
        <>
            <CommonPageWrapper>
                <HeadingWrapper>
                    <PageMainLabel>Riders</PageMainLabel>
                    <CustomBreadCrumb preData={breadCrumbItems} />
                </HeadingWrapper>

                <HeadingWrapper>
                    <FilterContainer>
                        <DropdownWrapper>
                            <AdminDropDown
                                label="Type"
                                placeholder="Select Type"
                                data={typeOptions}
                                onDataChange={handleTypeChange}
                                defaultValue={
                                    typeOptions.find(opt => opt.id === type) || typeOptions[0]
                                }
                            />
                        </DropdownWrapper>

                        <DateFilterWrapper>
                            <AdminDatePicker
                                title="From Date"
                                value={fromDate ? new Date(fromDate) : null}
                                onChange={handleFromDateChange}
                                dateFormat="MM-dd-yyyy"
                                maxdate={new Date()}
                            />
                            <AdminDatePicker
                                title="To Date"
                                value={toDate ? new Date(toDate) : null}
                                onChange={handleToDateChange}
                                dateFormat="MM-dd-yyyy"
                                disabled={!fromDate}
                                mindate={new Date(fromDate)}
                            />
                        </DateFilterWrapper>

                        <SearchWrapper>
                            <AdminSearch
                                placeholder="Search by name"
                                onSearchTextEntering={handleSearch}
                                searchTerm={localSearchTerm}
                                setSearchTerm={setLocalSearchTerm}
                            />
                        </SearchWrapper>

                        <AdminCustomButton
                            buttonType="primary"
                            isActive={false}
                            className="fill-button"
                            onClick={handleDownloadRegistrationReport}
                            disabled={
                                registrationReportData?.items?.length === 0 || downloadLoading
                            }
                        >
                            {downloadLoading ? 'Downloading...' : 'Download'}
                        </AdminCustomButton>
                    </FilterContainer>
                </HeadingWrapper>
                {(registrationReportData?.totalCount ?? 0) > 10 && (
                    <EntriesPerPageDropDownWrapper>
                        <AdminDropDown
                            label="No: of entries per page"
                            placeholder="Select entries"
                            onDataChange={(data: GeneralLookup) => {
                                const newPageSize = parseInt(data.id, 10);
                                setPageSize(newPageSize);
                                setPageSizeSelected(data);
                                setPageNumber(0);
                            }}
                            defaultValue={pageSizeSelected}
                            data={pageSizeOptions}
                            className="entries-dropdown-style"
                            popupStyles="entries-dropdown-popup"
                        />
                    </EntriesPerPageDropDownWrapper>
                )}
                {loading || downloadLoading ? (
                    <TableSkeletonLoader
                        rowContent={pageSize}
                        width={100}
                        height={20}
                        headingWidth={120}
                    />
                ) : (
                     hasViewPermission('1100101005') && (
                        <RegistrationReportTable
                            registrationReportData={registrationReportData}
                            pageNumber={pageNumber}
                            pageSize={pageSize}
                            onPageChange={handleTablePageChange}
                            type={type}
                            fromDate={fromDate}
                            toDate={toDate}
                            searchName={debounceSearch}
                            pageSizeSelected={pageSizeSelected}
                            transformedData={transformedData}
                            fetchRegistrationReport={fetchRegistrationReport}
                            isPaymentLoading={isPaymentLoading}
                        />
                    )
                )}
            </CommonPageWrapper>
        </>
    );
};

export default RegistrationReportPage;

const FilterContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 20px;
  flex-wrap: wrap;
  width: 100%;
  .custom-calendar-wrapper {
    min-height: unset !important;
  }
  .k-input-solid {
    height: 33px !important;
  }
`;

const DropdownWrapper = styled.div`
  min-width: 180px;
  flex-shrink: 0;
  .k-dropdownlist {
    height: 33px !important;
  }
`;

const DateFilterWrapper = styled.div`
  display: flex;
  gap: 15px;
  align-items: flex-end;
  flex-shrink: 0;
`;

const SearchWrapper = styled.div`
  flex: 1;
  min-width: 250px;
  max-width: 300px;
`;
const EntriesPerPageDropDownWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  padding-top: 10px;
  .SHdropdown {
    width: 160px;

    .label {
      margin-bottom: 8px;
      font-size: 13px;
      color: #475467;
      font-weight: 500;
    }

    .k-dropdownlist {
      max-height: 38px;
      border-radius: 8px !important;
    }

    .k-input-inner {
      padding: 0 8px !important;
      font-size: 13px;
    }

    .k-input-value-text {
      margin: 4px 0;
      font-size: 13px !important;
      color: #1e1e1e;
    }
  }
`;